import { useState } from 'react';
import type { MembershipInterestService } from '@beautyburst/leads';
import type { CustomerApiClient } from '../api-client.ts';
import { Button, Card, ErrorSummary, LoadingState } from '@beautyburst/ui';
import { FormField, errorsForSummary, useFormErrors, validateEmail } from '../forms.tsx';
import { semanticColor, space, typeStyle } from '@beautyburst/tokens';
import { customerHref } from '../base-path.ts';

export interface BeautyBurstPlusScreenProps {
  client: Pick<CustomerApiClient, 'checkout' | 'leads' | 'membership'>;
  marketId?: string | null;
  plans?: Awaited<ReturnType<CustomerApiClient['membership']['plans']>>;
  signedIn?: boolean;
}

function price(
  plan: Awaited<ReturnType<CustomerApiClient['membership']['plans']>>[number],
): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: plan.currency }).format(
    plan.priceMinor / 100,
  );
}

function interval(
  plan: Awaited<ReturnType<CustomerApiClient['membership']['plans']>>[number],
): string {
  const labels: Record<typeof plan.interval, string> = { MONTH: 'month' };
  return labels[plan.interval];
}

function benefitCopy(
  benefit: Awaited<
    ReturnType<CustomerApiClient['membership']['plans']>
  >[number]['benefits'][number],
): string {
  const delivery = `${benefit.deliveryType[0]}${benefit.deliveryType.slice(1).toLowerCase()}`;
  const minimum =
    benefit.minimumOrderMinor > 0
      ? ` on eligible groups of ${new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
        }).format(benefit.minimumOrderMinor / 100)} or more`
      : ' on eligible groups';
  if (benefit.waiveBaseFee)
    return `${delivery}: the configured base delivery fee is waived${minimum}.`;
  return `${delivery}: ${new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(benefit.feeReductionMinor / 100)} off the configured base delivery fee${minimum}.`;
}

/** Shows the configured recurring offering; interest capture is reserved for no-plan fallback. */
export function BeautyBurstPlusScreen({
  client,
  marketId = null,
  plans,
  signedIn = false,
}: BeautyBurstPlusScreenProps) {
  const [email, setEmail] = useState('');
  const [consent, setConsent] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [enrollingPlanId, setEnrollingPlanId] = useState<string | null>(null);
  const [membershipStatus, setMembershipStatus] = useState<string | null>(null);
  const [membershipError, setMembershipError] = useState<string | null>(null);
  const { errors, setErrors, clear } = useFormErrors();

  const submitInterest = async (
    interestKind: Parameters<MembershipInterestService['submit']>[0]['interestKind'],
  ) => {
    const emailError = validateEmail(email);
    if (emailError !== undefined) {
      setErrors({ 'plus-email': emailError });
      return;
    }
    const payload: Parameters<MembershipInterestService['submit']>[0] = {
      email: email.trim(),
      interestKind,
      marketId,
      leadSource: 'MEMBERSHIP_PAGE',
      consentMarketing: consent,
      consentAt: consent ? new Date() : null,
    };
    try {
      await client.leads.submitMembershipInterest(
        payload as unknown as Parameters<CustomerApiClient['leads']['submitMembershipInterest']>[0],
      );
      setSubmitted(true);
    } catch {
      setErrors({ 'plus-email': 'We could not record your interest. Please try again.' });
    }
  };

  const enroll = async (
    plan: Awaited<ReturnType<CustomerApiClient['membership']['plans']>>[number],
  ) => {
    setEnrollingPlanId(plan.id);
    setMembershipError(null);
    try {
      const token = await client.checkout.createPaymentToken({ provider: 'SANDBOX' });
      const outcome = await client.membership.enroll({
        planId: plan.id,
        provider: token.provider,
        paymentTokenReference: token.paymentTokenReference,
      });
      setMembershipStatus(
        outcome.membership.status === 'ACTIVE'
          ? `You are active in ${plan.name}.`
          : 'Your membership enrollment is being processed.',
      );
    } catch {
      setMembershipError(
        'We could not complete your membership enrollment. Please check your details and try again.',
      );
    } finally {
      setEnrollingPlanId(null);
    }
  };

  if (plans === undefined)
    return (
      <section aria-labelledby="plus-title">
        <h1 id="plus-title" style={{ ...typeStyle('headingLg'), color: semanticColor.textPrimary }}>
          Beauty Burst+
        </h1>
        <LoadingState label="Loading Beauty Burst+ membership" />
      </section>
    );

  if (plans.length > 0) {
    return (
      <section aria-labelledby="plus-title">
        <h1 id="plus-title" style={{ ...typeStyle('headingLg'), color: semanticColor.textPrimary }}>
          Beauty Burst+
        </h1>
        <p style={{ ...typeStyle('body'), color: semanticColor.textSecondary }}>
          Recurring membership with delivery benefits configured for eligible fulfilment groups.
        </p>
        {plans.map((plan) => (
          <Card key={plan.id}>
            <section aria-labelledby={`membership-plan-${plan.id}`}>
              <h2
                id={`membership-plan-${plan.id}`}
                style={{ ...typeStyle('headingSm'), color: semanticColor.textPrimary }}
              >
                {plan.name}
              </h2>
              <p style={{ ...typeStyle('headingMd'), color: semanticColor.textPrimary }}>
                {price(plan)} / {interval(plan)}
              </p>
              <h3 style={{ ...typeStyle('label'), color: semanticColor.textPrimary }}>
                Member delivery benefits
              </h3>
              <ul>
                {plan.benefits.map((benefit) => (
                  <li key={benefit.deliveryType}>{benefitCopy(benefit)}</li>
                ))}
              </ul>
              <p style={{ ...typeStyle('bodySm'), color: semanticColor.textSecondary }}>
                Benefits apply per eligible fulfilment or delivery group. Overnight and express
                groups receive only the benefit allowed by their configured rules, and required
                surcharges remain. Eligibility, availability, and offer terms may vary. This is not
                a promise that every delivery is free.
              </p>
              {signedIn ? (
                <Button
                  type="button"
                  disabled={enrollingPlanId !== null}
                  onClick={() => void enroll(plan)}
                >
                  {enrollingPlanId === plan.id
                    ? 'Starting membership…'
                    : `Join for ${price(plan)} / ${interval(plan)}`}
                </Button>
              ) : (
                <a href={customerHref('/sign-in')}>Sign in to join Beauty Burst+</a>
              )}
            </section>
          </Card>
        ))}
        {membershipStatus !== null ? <p role="status">{membershipStatus}</p> : null}
        {membershipError !== null ? <p role="alert">{membershipError}</p> : null}
      </section>
    );
  }

  return (
    <section aria-labelledby="plus-title">
      <h1 id="plus-title" style={{ ...typeStyle('headingLg'), color: semanticColor.textPrimary }}>
        Beauty Burst+
      </h1>
      <p style={{ ...typeStyle('body'), color: semanticColor.textSecondary }}>
        Beauty Burst+ is not configured for this market yet. Share your interest and we will keep
        you informed.
      </p>
      <section aria-labelledby="plus-benefits-title">
        <h2
          id="plus-benefits-title"
          style={{ ...typeStyle('headingSm'), color: semanticColor.textPrimary }}
        >
          Potential benefits when membership is available
        </h2>
        <ul>
          <li>Configured delivery benefits on eligible fulfilment groups.</li>
          <li>Benefits may differ for standard, express, and overnight delivery groups.</li>
        </ul>
        <p style={{ ...typeStyle('bodySm'), color: semanticColor.textSecondary }}>
          Eligibility, availability, and offer terms may vary. This is not a promise that every
          delivery is free.
        </p>
      </section>

      <Card>
        {submitted ? (
          <p role="status">Thanks — we recorded your Beauty Burst+ interest.</p>
        ) : (
          <form
            noValidate
            onSubmit={(event) => {
              event.preventDefault();
              void submitInterest('WAITLIST');
            }}
            style={{ display: 'grid', gap: space[4] }}
          >
            <ErrorSummary errors={errorsForSummary(errors)} />
            <FormField id="plus-email" label="Email address" error={errors['plus-email']} required>
              <input
                type="email"
                autoComplete="email"
                value={email}
                onChange={(event) => {
                  setEmail(event.currentTarget.value);
                  clear('plus-email');
                }}
              />
            </FormField>
            <label
              style={{ display: 'flex', gap: space[2], minHeight: '44px', alignItems: 'center' }}
            >
              <input
                type="checkbox"
                checked={consent}
                onChange={(event) => setConsent(event.currentTarget.checked)}
              />
              Email me Beauty Burst+ updates.
            </label>
            <div style={{ display: 'flex', gap: space[3], flexWrap: 'wrap' }}>
              <Button type="submit">Join waitlist</Button>
              <Button
                type="button"
                variant="secondary"
                onClick={() => void submitInterest('NOTIFY_ME')}
              >
                Notify me
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={() => void submitInterest('LEARN_MORE')}
              >
                Learn more
              </Button>
            </div>
          </form>
        )}
      </Card>
    </section>
  );
}
